import { useTranslation } from '@frontend/i18n';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, Heading, Modal, ModalControlModalProps, Text } from '@frontend/design-system';
import {
  BookingSiteSetupReasonCode,
  useBookingSiteSetupReasonAction,
} from '../../hooks/booking-site/use-booking-site-setup-reason-action';

interface BookingSiteSetupInstructionsProps extends CommonHTMLAttributes {
  locationId: string;
  isMultiLocation?: boolean;
  renderAsModal?: boolean;
  modalProps?: ModalControlModalProps;
  reasonCode?: BookingSiteSetupReasonCode;
  isScheduleDefinitionsEnabled?: boolean;
}

export const BookingSiteSetupInstructions = ({
  locationId,
  isMultiLocation,
  renderAsModal = false,
  modalProps,
  reasonCode,
  isScheduleDefinitionsEnabled = false,
  ...props
}: BookingSiteSetupInstructionsProps) => {
  const { t } = useTranslation('schedule');

  const resolvedReasonCode: BookingSiteSetupReasonCode =
    reasonCode ??
    (isScheduleDefinitionsEnabled ? 'NO_SCHEDULE_DEFINITION_AVAILABILITY' : 'NO_PROVIDER_APPOINTMENT_MAPPING');
  const title = isScheduleDefinitionsEnabled ? t('Setup Availability') : t('Connect Providers & Appointments');
  const shouldShowNoReasonHint = !reasonCode && isScheduleDefinitionsEnabled;

  const { message, ctaLabel, shouldShowAction, onCtaClick } = useBookingSiteSetupReasonAction({
    reasonCode: resolvedReasonCode,
    locationId,
    isScheduleDefinitionsEnabled,
    isMultiLocation,
  });

  const instructionsContent = (
    <div {...props}>
      {!renderAsModal && (
        <section css={{ marginBottom: theme.spacing(3) }}>
          <Heading level={3}>{title}</Heading>
          <Text size='medium' css={{ color: theme.colors.neutral70 }}>
            ⏱️ {t('Est. time: 5–10 minutes')}
          </Text>
        </section>
      )}
      <section css={{ marginBottom: theme.spacing(3), paddingRight: theme.spacing(1) }}>
        {message.secondaryMessage || message.tertiaryMessage ? (
          <ul css={{ paddingLeft: theme.spacing(3) }}>
            <Text as='li' size='large'>
              {message.primaryMessage}
            </Text>
            {message.secondaryMessage && (
              <Text as='li' size='large'>
                {message.secondaryMessage}
              </Text>
            )}
            {message.tertiaryMessage && (
              <Text as='li' size='large'>
                {message.tertiaryMessage}
              </Text>
            )}
          </ul>
        ) : (
          <Text size='large'>{message.primaryMessage}</Text>
        )}
      </section>
      {!isScheduleDefinitionsEnabled && (
        <>
          <section css={{ marginBottom: theme.spacing(3) }}>
            <Text weight='bold' size='large'>
              {t('Why it matters:')}
            </Text>
            <ul css={{ paddingLeft: theme.spacing(3) }}>
              <Text as='li' size='large'>
                ✅ {t('Patients book with the right providers')}
              </Text>
              <Text as='li' size='large'>
                🗓️ {t('Accurate availability shown online')}
              </Text>
              <Text as='li' size='large'>
                😊 {t('Smoother experience for everyone')}
              </Text>
            </ul>
          </section>
          <section>
            <Text css={{ marginTop: theme.spacing(3) }} weight='bold' size='large'>
              {t('Quick Steps:')}
            </Text>
            <ol css={{ paddingLeft: theme.spacing(3) }}>
              <li>
                <Text size='large'>{t('Click “Start Mapping” below')}</Text>
              </li>
              <li>
                <Text size='large'>{t(`Find the Appointment type's row and select "Edit"`)}</Text>
              </li>
              <li>
                <Text size='large'>{t('Select available providers for that appointment type')}</Text>
              </li>
              <li>
                <Text size='large'>{t('Save your changes')}</Text>
              </li>
            </ol>
          </section>
        </>
      )}
      {shouldShowNoReasonHint && shouldShowAction && !!ctaLabel && (
        <section css={{ marginTop: theme.spacing(3) }}>
          <Text size='large'>
            {t('Click "Create Availability" to ensure your availability rules are correctly configured.')}
          </Text>
        </section>
      )}
      {!renderAsModal && shouldShowAction && !!ctaLabel && (
        <Button onClick={onCtaClick} css={{ marginTop: theme.spacing(3) }}>
          {ctaLabel}
        </Button>
      )}
    </div>
  );

  if (!renderAsModal) {
    return instructionsContent;
  }

  if (!modalProps) {
    return instructionsContent;
  }

  return (
    <Modal maxWidth={600} {...modalProps}>
      <Modal.Header onClose={modalProps?.onClose}>
        <Heading level={2}>{title}</Heading>
        <Text size='medium' weight='regular' color='light'>
          ⏱️ {t('Est. time: 5–10 minutes')}
        </Text>
      </Modal.Header>
      <Modal.Body>{instructionsContent}</Modal.Body>
      <Modal.Footer
        primary={
          shouldShowAction && !!ctaLabel
            ? {
                label: ctaLabel,
                onClick: onCtaClick,
              }
            : undefined
        }
        secondary={{
          label: shouldShowAction && !!ctaLabel ? t('Cancel') : t('Close'),
        }}
      />
    </Modal>
  );
};
